<template>
    <div class="field-info-dialog-root">
        <el-dialog
            :visible.sync="innerVisible"
            title="地块详情"
            width="800px"
            append-to-body
            :modal="true"
            :close-on-click-modal="false"
            custom-class="field-info-dialog"
            :center="true"
            @close="handleClose"
        >
            <div class="field-dialog">
                <div class="dialog-body" v-if="plotId">
                    <div class="basic-info-wrapper">
                        <div class="section-title">基本信息</div>
                        <div class="info-grid">
                            <div class="info-item">
                                <span class="info-label">地块名称：</span>
                                <span class="info-value">{{ basicInfo.name || basicInfo.plotName || '--' }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">面积：</span>
                                <span class="info-value">{{ basicInfo.area ? basicInfo.area + '亩' : '--' }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="crop-scroll-area">
                        <el-collapse
                            v-model="activeCrop"
                            class="crop-collapse"
                            @change="handleCropChange"
                        >
                        <el-collapse-item
                            v-for="crop in cropList"
                            :key="crop.id"
                            :name="String(crop.id)"
                        >
                            <template slot="title">
                                <div class="crop-header">
                                    <span class="crop-title">种植作物：{{ crop.cropName || '--' }}</span>
                                    <span class="crop-end-date">
                                        <span class="info-label">种植日期：</span>
                                        <span class="info-value">{{ crop.plantingDate || '--' }}</span>
                                    </span>
                                </div>
                            </template>

                            <div class="info-row-line">
                                <div class="info-item">
                                    <span class="info-label">预计采收日期：</span>
                                    <span class="info-value">{{ crop.expectedHarvestDate || '--' }}</span>
                                </div>
                                <div class="btn-group">
                                    <el-button
                                        type="primary"
                                        size="small"
                                        class="action-btn primary-btn"
                                        @click="handleViewSource(crop)"
                                    >查看溯源码</el-button>
                                </div>
                            </div>

                            <div class="section-title">农事作业过程记录</div>
                            <div v-if="cropState[String(crop.id)] === 'loading'" class="loading-tip">加载中...</div>
                            <el-timeline
                                v-else-if="cropState[String(crop.id)] === 'loaded' && operationsMap[crop.id] && operationsMap[crop.id].length > 0"
                                class="operation-timeline"
                            >
                                <el-timeline-item
                                    v-for="(op, index) in operationsMap[crop.id]"
                                    :key="op.id || index"
                                    class="operation-timeline-item"
                                >
                                    <div class="operation-header">
                                        <span class="operation-name">{{ getOpTypeLabel(op.operationType) }}</span>
                                        <span class="operation-date">{{ op.operationDate }}</span>
                                    </div>
                                    <div
                                        class="operation-info"
                                        v-if="op.operationMode || op.materialName || getOpQuantity(op)"
                                    >
                                        <div class="info-row">
                                            <template v-if="op.operationMode">
                                                <span class="row-label">作业方式：</span>
                                                <span class="row-value">{{ getOpModeLabel(op.operationMode) }}</span>
                                            </template>
                                            <template v-if="op.materialName">
                                                <span
                                                    class="row-label"
                                                    :style="op.operationMode ? 'margin-left: 20px;' : ''"
                                                >物料：</span>
                                                <span class="row-value">{{ op.materialName }}</span>
                                            </template>
                                            <template v-if="getOpQuantity(op)">
                                                <span
                                                    class="row-label"
                                                    style="margin-left: 20px;"
                                                >数量：</span>
                                                <span class="row-value">{{ getOpQuantity(op) }}</span>
                                            </template>
                                        </div>
                                    </div>
                                    <div
                                        class="operation-photos"
                                        v-if="getOpPhotos(op).length"
                                    >
                                        <div
                                            v-for="(photo, pIdx) in getOpPhotos(op)"
                                            :key="pIdx"
                                            class="photo-thumb"
                                        >
                                            <img :src="photo" alt="" />
                                        </div>
                                    </div>
                                </el-timeline-item>
                            </el-timeline>
                            <div v-else-if="cropState[String(crop.id)] === 'loaded'" class="empty-tip">暂无农事作业记录</div>
                        </el-collapse-item>
                    </el-collapse>
                    </div>
                </div>
            </div>
        </el-dialog>

        <!-- 溯源二维码弹窗：放在外层 el-dialog 外部，避免被 overflow:hidden 裁剪 -->
        <SourceQrcodeDialog
            :visible.sync="qrcodeVisible"
            :plantingId="currentCropForQrcode && currentCropForQrcode.id"
            :data="currentCropForQrcode"
            title="溯源二维码"
        />
    </div>
</template>

<script>
import NongyeApi from '../api';
import SourceQrcodeDialog from '@/components/custom-qrcode/SourceQrcodeDialog.vue';
import DicApi from '@/api/system/dic';

export default {
    name: 'FieldInfoDialog',
    components: {
        SourceQrcodeDialog,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        plotId: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        return {
            innerVisible: false,
            basicInfo: {},
            cropList: [],
            operationsMap: {},
            cropState: {},
            activeCrop: [],
            qrcodeVisible: false,
            currentCropForQrcode: null,
            operationTypeMap: {},
            operationModeMap: {},
        };
    },
    watch: {
        visible: {
            immediate: true,
            handler(val) {
                this.innerVisible = val;
            },
        },
        innerVisible(val) {
            this.$emit('update:visible', val);
            if (val && this.plotId) {
                this.loadAll();
            } else if (!val) {
                this.basicInfo = {};
                this.cropList = [];
                this.operationsMap = {};
                this.cropState = {};
                this.activeCrop = [];
            }
        },
        plotId: {
            immediate: true,
            handler() {
                if (this.innerVisible && this.plotId) {
                    this.loadAll();
                }
            },
        },
    },
    methods: {
        async loadDicts() {
            try {
                const [typeRes, modeRes] = await Promise.all([
                    DicApi.fetchDictByType('dict_agri_farm_type'),
                    DicApi.fetchDictByType('dict_agri_farm_mode'),
                ]);
                if (typeRes && typeRes.dicMap && Object.keys(typeRes.dicMap).length) {
                    this.operationTypeMap = typeRes.dicMap;
                }
                if (modeRes && modeRes.dicMap && Object.keys(modeRes.dicMap).length) {
                    this.operationModeMap = modeRes.dicMap;
                }
            } catch (e) {
                console.warn('[FieldInfoDialog] 字典加载失败', e);
            }
        },
        async loadAll() {
            await Promise.all([
                this.loadDicts(),
                this.loadBasicInfo(),
                this.loadPlantings(),
            ]);
            if (this.cropList.length > 0) {
                this.activeCrop = [String(this.cropList[0].id)];
                // 程序化设置 activeCrop 不会触发 @change，需手动加载首条记录的农事作业
                this.loadOperationsForCrop(this.cropList[0]);
            }
        },
        async loadBasicInfo() {
            try {
                const res = await NongyeApi.getAgriPlot(this.plotId);
                this.basicInfo = res?.data || res || {};
            } catch (e) {
                this.basicInfo = {};
            }
        },
        async loadPlantings() {
            try {
                const res = await NongyeApi.listAgriPlotPlanting({ plotId: this.plotId });
                const list = res?.rows || res?.data || res || [];
                this.cropList = Array.isArray(list) ? list : [];
            } catch (e) {
                this.cropList = [];
            }
        },
        handleCropChange(activeNames) {
            if (!Array.isArray(activeNames)) return;
            activeNames.forEach((id) => {
                const crop = this.cropList.find((c) => String(c.id) === String(id));
                if (crop && !this.cropState[id]) {
                    this.loadOperationsForCrop(crop);
                }
            });
        },
        async loadOperationsForCrop(crop) {
            const id = String(crop.id);
            this.$set(this.cropState, id, 'loading');
            try {
                const res = await NongyeApi.listAgriFarmingRecord({
                    pageNum: 1,
                    pageSize: 1000,
                    plotId: this.plotId,
                    plantingId: crop.id,
                });
                const list = res?.rows || res?.data || (Array.isArray(res) ? res : []);
                this.$set(this.operationsMap, crop.id, Array.isArray(list) ? list : []);
                this.$set(this.cropState, id, 'loaded');
            } catch (e) {
                console.error('[FieldInfoDialog] loadOperationsForCrop 异常', e);
                this.$set(this.operationsMap, crop.id, []);
                this.$set(this.cropState, id, 'loaded');
            }
        },
        // --- 字典/格式化辅助方法 ---
        getOpTypeLabel(code) {
            if (!code) return '';
            return this.operationTypeMap[code] || code;
        },
        getOpModeLabel(code) {
            if (!code) return '';
            return this.operationModeMap[code] || code;
        },
        getOpQuantity(record) {
            if (record.materialAmount == null) return '';
            return `${record.materialAmount}${record.materialUnit ? ` ${record.materialUnit}` : ''}`;
        },
        getOpPhotos(record) {
            const source = record.picturesPath != null ? record.picturesPath : record.pictures;
            if (!source) return [];
            if (Array.isArray(source)) return source.filter(Boolean);
            if (typeof source === 'string') return source.split(',').map((s) => s.trim()).filter(Boolean);
            return [];
        },
        handleClose() {
            this.innerVisible = false;
            this.$emit('close');
        },
        handleViewSource(crop) {
            if (!crop) return;
            this.currentCropForQrcode = {
                ...crop,
                plotName: this.basicInfo.name || this.basicInfo.plotName || '',
                plotId: this.plotId,
            };
            this.qrcodeVisible = true;
        },
    },
};
</script>

<style lang="scss" scoped>
.field-dialog {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.dialog-body {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 10px 16px 0;
    color: #c7d6f0;
}

.basic-info-wrapper {
    flex-shrink: 0;
}

.crop-scroll-area {
    flex: 1 1 0;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: 24px;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(73, 240, 253, 0.6);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: rgba(73, 240, 253, 0.9);
    }

    &::-webkit-scrollbar-track {
        background: rgba(7, 17, 80, 0.4);
        border-radius: 4px;
    }
}

.section-title {
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    margin: 8px 0 14px;
    letter-spacing: 1px;
}

.info-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0;
    margin-bottom: 20px;
    padding: 14px 18px;
    background: #071150;
    border: 1px solid #1a47a4;
    border-radius: 4px;
    box-shadow: inset 0px 0px 10px 0px rgba(73, 240, 253, 0.25);
}

.info-item {
    display: flex;
    align-items: center;
    font-size: 13px;
    line-height: 1.8;
    flex: 1 1 45%;
    min-width: 45%;
}

.info-label {
    color: #8fa4cd;
}

.info-value {
    color: #fff;
    font-weight: 500;
}

.crop-collapse {
    margin-bottom: 4px;
    border: none !important;

    ::v-deep .el-collapse-item__header {
        height: auto;
        line-height: 1.6;
        padding: 12px 16px;
        background: #071150;
        border: 1px solid #1a47a4 !important;
        border-radius: 4px;
        color: #fff;
        font-size: 14px;
        box-shadow: inset 0px 0px 10px 0px rgba(73, 240, 253, 0.25);
        outline: none !important;

        &:hover,
        &.focusing,
        &:focus {
            border-color: #49f0fd !important;
            outline: none !important;
        }

        .crop-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            padding-right: 32px;
            cursor: pointer;
        }

        .crop-title {
            font-weight: 600;
            color: #5bf0fc;
        }

        .crop-end-date {
            display: flex;
            align-items: center;
            font-size: 13px;
            font-weight: 400;
            color: #c7d6f0;

            .info-label {
                color: #8fa4cd;
            }

            .info-value {
                color: #fff;
            }
        }
    }

    ::v-deep .el-collapse-item.is-active .el-collapse-item__header {
        border-color: #49f0fd !important;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

    ::v-deep .el-collapse-item__wrap {
        background: #071150;
        border: 1px solid #1a47a4 !important;
        border-top: none !important;
        border-radius: 0 0 4px 4px;
        box-shadow: inset 0px 0px 10px 0px rgba(73, 240, 253, 0.25) !important;
        overflow: visible;
    }

    ::v-deep .el-collapse-item__content {
        padding: 14px 18px;
    }

    ::v-deep .el-collapse-item__arrow {
        color: #49f0fd;
    }
}

.info-row-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    .info-item {
        flex: 0 1 auto;
        min-width: 0;
    }

    .btn-group {
        flex: 0 0 auto;
        margin-bottom: 0;
    }
}

.btn-group {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    justify-content: center;
    padding-top: 8px;
}

.action-btn {
    border-radius: 4px;
    font-size: 14px;
    padding: 8px 18px;
    min-width: 144px;

    &.primary-btn {
        color: #000000;
        background: #49f0fd;
        border: 1px solid #49f0fd;

        &:hover {
            background: #5bf0fc;
            border-color: #5bf0fc;
        }
    }

    &.default-btn {
        color: #49f0fd;
        border: 1px solid #49f0fd;
        background: rgba(73, 240, 253, 0.2);

        &:hover {
            background: rgba(73, 240, 253, 0.3);
            color: #5bf0fc;
            border-color: #5bf0fc;
        }
    }
}

.operation-timeline {
    padding-left: 4px;

    ::v-deep .el-timeline-item__node {
        background-color: #49f0fd;
        border-color: #49f0fd;
        box-shadow: 0 0 6px rgba(73, 240, 253, 0.6);
        width: 12px;
        height: 12px;
        left: -3px;
    }

    ::v-deep .el-timeline-item__wrapper {
        padding-left: 24px;
        top: -2px;
    }

    ::v-deep .el-timeline-item__content {
        color: #fff;
    }

    ::v-deep .el-timeline-item__tail {
        border-left: 2px solid rgba(73, 240, 253, 0.5);
        left: 1px;
    }
}

.operation-timeline-item {
    padding-bottom: 20px;
}

.operation-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.operation-name {
    font-size: 15px;
    font-weight: 600;
    color: #fff;
}

.operation-date {
    font-size: 12px;
    color: #8fa4cd;
}

.operation-info {
    margin-bottom: 10px;
}

.info-row {
    display: flex;
    align-items: center;
    font-size: 12px;
    line-height: 1.8;
}

.row-label {
    color: #8fa4cd;
}

.row-value {
    color: #c7d6f0;
}

.operation-photos {
    display: flex;
    gap: 8px;
}

.photo-thumb {
    width: 80px;
    height: 60px;
    border-radius: 4px;
    overflow: hidden;
    background: rgba(26, 71, 164, 0.3);
    border: 1px solid #1a47a4;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.empty-tip {
    text-align: center;
    padding: 24px 0;
    color: #8fa4cd;
    font-size: 13px;
}

.loading-tip {
    text-align: center;
    padding: 20px 0;
    color: #5bf0fc;
    font-size: 13px;
}
</style>

<style lang="scss">
// custom-class 应用在 el-dialog 根元素上，与 .el-dialog 是同一元素
// 因为 append-to-body，弹窗 DOM 挂到 body 根级别，样式需用全局作用域
.field-info-dialog {
    background: #071150;
    color: white;
    box-shadow: inset 0px 0px 10px 0px #49f0fd;
    opacity: 1;
    border: 1px solid #49f0fd;
    border-radius: 4px;
    height: 700px !important;
    max-height: 85vh !important;
    display: flex !important;
    flex-direction: column !important;

    .el-dialog__header {
        padding: 16px;
        border-bottom: #49f0fd 1px solid;
        box-shadow: inset 0px -1px 5px #49f0fd;
        background: transparent;
        position: relative !important;
        flex-shrink: 0;

        .el-dialog__title {
            font-size: 16px;
            font-weight: 600;
            color: #fff;
            line-height: 24px;
            letter-spacing: 2px;
        }

        .el-dialog__headerbtn {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            right: 16px;
            z-index: 1;

            .el-dialog__close {
                color: #49f0fd;
                font-size: 22px;
                font-weight: 300;

                &:hover {
                    color: #5bf0fc;
                }
            }
        }
    }

    .el-dialog__body {
        padding: 0 !important;
        flex: 1 1 auto !important;
        min-height: 0 !important;
        overflow: hidden !important;
        display: flex !important;
        flex-direction: column !important;
        color: #fff;
        position: relative;
        background: transparent;
    }
}
</style>
